import React from 'react';
import { formatMessage } from '@99designs/frontend-components/i18n';
import TrackScrollToPageElement from '../../../../shared/components/Analytics/TrackScrollToPageElement';
import Box from '../../../../shared/components/Box';
import { ResponsiveSpace } from '../../../../shared/components/Box/types/styles';
import Heading from '../../../../shared/components/Heading';
import ContestEntries from '../ContestEntries';
import { CategoryType, EntryType } from '../ContestOverviewData';
import { StatsSectionFAQs } from '../FAQs';
import StatsItem from '../StatsItem';

interface Props {
    designCount: number;
    designerCount: number;
    winnerCount: number;
    entries: EntryType[];
    category: CategoryType;
    isCompleted: boolean;
}

function StatsSection({
    entries,
    designCount,
    designerCount,
    winnerCount,
    category,
    isCompleted,
}: Props) {
    const hasFourDigitCount = designCount > 999 || designerCount > 999 || winnerCount > 999;
    const hasThreeDigitCount = designCount > 99 || designerCount > 99 || winnerCount > 99;

    return (
        <>
            <TrackScrollToPageElement pageName="Contest Overview" pageElement="Stats Section">
                <div className="row" data-testid="stats-section">
                    <div className="column small-12 medium-6 medium-offset-3 normal-5 normal-offset-4 large-4 large-offset-4">
                        <Box paddingBottom={['space3', 'space10', 'space12', 'space12']}>
                            <Heading type="h2" variations={['h1', 'no-margin']}>
                                {isCompleted
                                    ? __('Designers across the globe delivered design magic.')
                                    : __(
                                          'Right now, designers across the globe are working on design concepts…'
                                      )}
                            </Heading>
                        </Box>
                    </div>
                </div>
            </TrackScrollToPageElement>
            <Box
                paddingTop={['space11', 'space12', 'space14', 'space14']}
                paddingBottom={['space3', 'space3', 'space15', 'space15']}
            >
                <ContestEntries
                    entries={isCompleted ? entries : undefined}
                    category={category}
                    withLargeStats={hasThreeDigitCount}
                />
            </Box>
            {isCompleted && (
                <div className="row">
                    <div className="column">
                        <Box
                            paddingTop={['space10', 'space10', 'space10', 'space10']}
                            paddingBottom={['space10', 'space10', 'space12', 'space12']}
                            textAlign={['left', 'center', 'center', 'center']}
                        >
                            <StatsItem
                                count={designCount}
                                title={
                                    designCount === 1 ? __('design entry') : __('design entries')
                                }
                                description={__(
                                    'You’ll get loads of concepts from experienced designers around the globe.'
                                )}
                                reducedDefaultFontSize={hasFourDigitCount}
                            />
                            <StatsItem
                                count={designerCount}
                                title={designerCount === 1 ? __('designer') : __('designers')}
                                description={formatMessage(
                                    __(
                                        'Work with talented, professional {categoryTitle} designers to turn your ideas into reality.'
                                    ),
                                    { categoryTitle: category.title }
                                )}
                                reducedDefaultFontSize={hasFourDigitCount}
                            />
                            <StatsItem
                                count={winnerCount}
                                title={winnerCount === 1 ? __('winner') : __('winners')}
                                description={formatMessage(
                                    __(
                                        'Select your favorite custom {categoryTitle} (Or two! Or three!). And the design is all yours.'
                                    ),
                                    { categoryTitle: category.title }
                                )}
                                reducedDefaultFontSize={hasFourDigitCount}
                            />
                        </Box>
                    </div>
                </div>
            )}
            <div className="row">
                <div className="column large-4 normal-4 medium-12 small-12 large-offset-2 normal-offset-2">
                    <Box
                        paddingTop={['none', 'none', 'space10', 'space10']}
                        paddingBottom={getBottomBoxSpacing(isCompleted ? entries.length : 0)}
                    >
                        <StatsSectionFAQs />
                    </Box>
                </div>
            </div>
        </>
    );
}

function getBottomBoxSpacing(entriesLength: number) {
    if (entriesLength > 8) {
        return ['space3', 'space3', '400px', '400px'] as ResponsiveSpace;
    } else if (entriesLength > 0) {
        return ['space3', 'space3', 'space3', 'space3'] as ResponsiveSpace;
    }
    return ['none', 'none', '520px', '520px'] as ResponsiveSpace;
}

export default StatsSection;
